import { observer } from 'mobx-react-lite';
import { useEffect, useState } from 'react';
import { useInView } from 'react-intersection-observer';
import awards1 from '../../../assets/awards1.svg';
import awards2 from '../../../assets/awards2.svg';
import LeftBeigeEgg from '../../../assets/LeftBeigeEgg.webp';
import leftEgg from '../../../assets/leftEgg.webp';
import rightEgg from '../../../assets/rightEgg.webp';
import { useMst } from '../../../models/rootModel';
import { PaywallMeta } from '../../../types/paywall';
import { AbsoluteHeader } from '../../common/AbsoluteHeader/AbsoluteHeader';
import { MainFunnelButtonV2 } from '../../common/v2/MainFunnelButtonV2';
import { BaseStepV3 } from '../../common/v3/BaseStepV3';
import { BaseStepPropsV3 } from '../../common/v3/BaseStepV3.types';
import { MainButtonVariant } from '../../styledComponent/Buttons.style';

import { PickPlanSummaryCard } from '../../common/PickPlanSummaryCard';
import { AdvantagesSection } from '../PickAProductContentV2/sections/AdvantagesSection';
import { GuidesSection } from '../PickAProductContentV2/sections/GuidesSection';
import { StoriesSection } from '../PickAProductContentV2/sections/StoriesSection';
import {
  PickAProductContentV3ContentDesktopLeftBeigeEggBg,
  PickAProductContentV3ContentDesktopLeftEggBg,
  PickAProductContentV3ContentDesktopRightEggBg,
  PickAProductContentV3ContentWrapper,
  PickAProductContentV3DesktopCtaSection,
  PickAProductContentV3DesktopWrapper,
  PickAProductContentV3Info,
  PickAProductContentV3Label,
  PickAProductContentV3LabelWrapper,
  PickAProductContentV3MobileCtaSection,
  PickAProductContentV3MobileCtaSectionContent,
  PickAProductContentV3SectionAwardImg,
  PickAProductContentV3SectionAwards,
  PickAProductContentV3StickyHeader,
  PickAProductContentV3StickyHeaderButtonWrapper,
  PickAProductContentV3StickyHeaderContent,
  PickAProductContentV3StickyInfo,
  PickAProductContentV3TrustedTitle,
} from './PickAProductContentV3.style';
import { PickPlanSectionV2 } from './sections/PickAPlanSectionV2';

interface PickAProductContentV3ContentProps extends BaseStepPropsV3 {
  handleAbsoluteBack: () => void;
  handleSubmit: (position: string) => void;
  pickAProduct: (pp: string, id: string) => void;
  planPositions: string[];
  data: PaywallMeta;
  useAbsoluteHeaderNavigation?: boolean;
}

export const PickAProductContentV3 = observer((props: PickAProductContentV3ContentProps) => {
  const { data, planPositions, useAbsoluteHeaderNavigation, handleSubmit, handleAbsoluteBack, isActive, pickAProduct } =
    props;

  const { UserPayment } = useMst();

  const [showStickyHeader, setShowStickyHeader] = useState(false);

  const { ref: submitButtonRef, inView: isSubmitButtonInView } = useInView();

  const handleStickyHeaderSubmit = () => {
    handleSubmit('scrolledHeader');
  };

  const handleMainSubmit = () => {
    handleSubmit('main');
  };

  useEffect(() => {
    if (!isActive) {
      setShowStickyHeader(false);
      return;
    }

    // to avoid flickering at the beginning of the step
    const timeout = setTimeout(() => {
      setShowStickyHeader(!isSubmitButtonInView);
    }, 300);

    return () => {
      clearTimeout(timeout);
    };
  }, [isActive, isSubmitButtonInView]);

  return (
    <BaseStepV3 {...props} addSpacingNeededForMobileButton={false}>
      <AbsoluteHeader onBack={useAbsoluteHeaderNavigation ? handleAbsoluteBack : undefined} />
      <PickAProductContentV3StickyHeader isVisible={showStickyHeader}>
        <PickAProductContentV3StickyHeaderContent>
          <PickAProductContentV3StickyInfo>{data.generalData.label}</PickAProductContentV3StickyInfo>
          <PickAProductContentV3StickyHeaderButtonWrapper>
            <MainFunnelButtonV2
              id="mainCta_pickPlan_sticky"
              text={data.generalData.button}
              onClick={handleStickyHeaderSubmit}
              disabled={false}
              isLoading={false}
              variant={MainButtonVariant.Purple}
            />
          </PickAProductContentV3StickyHeaderButtonWrapper>
        </PickAProductContentV3StickyHeaderContent>
      </PickAProductContentV3StickyHeader>

      <PickAProductContentV3ContentWrapper>
        <PickAProductContentV3ContentDesktopRightEggBg src={rightEgg} />
        <PickAProductContentV3ContentDesktopLeftEggBg src={leftEgg} />
        <PickAProductContentV3ContentDesktopLeftBeigeEggBg src={LeftBeigeEgg} />

        <PickPlanSectionV2
          chosenPlanPosition={UserPayment.chosenPlanPosition}
          stripePlans={UserPayment.stripePlans}
          planPositions={planPositions}
          data={data.generalData}
          pickAProduct={pickAProduct}
        />

        <PickAProductContentV3LabelWrapper>
          <PickAProductContentV3Label>Enjoy unlimited group sessions for your first week; </PickAProductContentV3Label>
          <PickAProductContentV3Label bold>
            your selected plan's session limits apply after the trial
          </PickAProductContentV3Label>
        </PickAProductContentV3LabelWrapper>

        <PickAProductContentV3DesktopCtaSection>
          <PickAProductContentV3DesktopWrapper ref={submitButtonRef}>
            <MainFunnelButtonV2
              id="mainCta_pickPlan_main"
              text={data.generalData.button}
              onClick={handleMainSubmit}
              disabled={false}
              isLoading={false}
              variant={MainButtonVariant.Black}
            />
            <PickAProductContentV3Info>{data.generalData.label}</PickAProductContentV3Info>
          </PickAProductContentV3DesktopWrapper>
        </PickAProductContentV3DesktopCtaSection>

        <PickAProductContentV3TrustedTitle>Trusted 160,000+ people</PickAProductContentV3TrustedTitle>

        <PickAProductContentV3SectionAwards>
          <PickAProductContentV3SectionAwardImg src={awards1} alt="awards1" />
          <PickAProductContentV3SectionAwardImg src={awards2} alt="awards2" />
        </PickAProductContentV3SectionAwards>

        <StoriesSection data={data.storiesSection} withTitles={false} withBottomBorder={false} />
        <AdvantagesSection data={data.advantagesSection} withRightEgg={false} withPaddingBottom={false} />
        <GuidesSection data={data.guidesSection} />

        <PickPlanSummaryCard />
      </PickAProductContentV3ContentWrapper>
      <PickAProductContentV3MobileCtaSection>
        <PickAProductContentV3MobileCtaSectionContent>
          <MainFunnelButtonV2
            id="mainCta_pickPlan_mobile"
            text={data.generalData.button}
            onClick={handleMainSubmit}
            disabled={false}
            isLoading={false}
            variant={MainButtonVariant.Black}
          />
          <PickAProductContentV3Info>{data.generalData.label}</PickAProductContentV3Info>
        </PickAProductContentV3MobileCtaSectionContent>
      </PickAProductContentV3MobileCtaSection>
    </BaseStepV3>
  );
});
